import { Container } from "react-bootstrap";
import { Helmet } from "react-helmet";
import styles from "../styles/pages/shippingpolicy.module.css";

const ShippingPolicy = () => {
    const canonicalUrl =
        "https://myonlineshopping.co.in/shipping-policy";

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Shipping Policy - Mohit's Shopping App",
        description:
            "Shipping and delivery information for purchases completed through Amazon from Mohit's Shopping App.",
        url: canonicalUrl,
        isPartOf: {
            "@type": "WebSite",
            name: "Mohit's Shopping App",
            url: "https://myonlineshopping.co.in/",
        },
    };

    const sections = [
        {
            id: "shipping-works",
            number: "01",
            title: "How Shipping Works",
            text: [
                "Mohit's Shopping App is an Amazon affiliate website that helps visitors discover products and access product pages on Amazon.",
                "When you click an affiliate product link, you may be redirected to Amazon where you can independently complete your purchase.",
                "Shipping and delivery are handled by Amazon and/or the applicable seller and delivery service.",
            ],
        },
        {
            id: "delivery-estimates",
            number: "02",
            title: "Delivery Estimates",
            text: [
                "Estimated delivery dates can depend on your delivery location, product availability, seller, shipping method, and other factors.",
                "Please check the delivery estimate shown on Amazon before completing your purchase.",
            ],
        },
        {
            id: "shipping-charges",
            number: "03",
            title: "Shipping Charges",
            text: [
                "Any shipping charges, delivery fees, Prime benefits, or other applicable charges are determined by Amazon according to the specific order and delivery address.",
            ],
        },
        {
            id: "order-tracking",
            number: "04",
            title: "Order Tracking",
            text: [
                "Once an order has been placed on Amazon, customers can use the relevant Amazon order tracking and account features to monitor delivery status.",
            ],
        },
        {
            id: "delayed-orders",
            number: "05",
            title: "Delayed or Missing Orders",
            text: [
                "If an Amazon order is delayed, missing, or experiencing a delivery issue, please contact Amazon through the support options associated with your order.",
            ],
        },
        {
            id: "damaged-delivery",
            number: "06",
            title: "Damaged Delivery",
            text: [
                "If a product arrives damaged or there is an issue with the delivered order, please use Amazon's order support, return, replacement, or customer service options.",
            ],
        },
        {
            id: "our-role",
            number: "07",
            title: "Our Role",
            text: [
                "We provide product discovery and referral links. We do not control Amazon inventory, warehouses, sellers, courier services, delivery schedules, or shipping charges.",
            ],
        },
        {
            id: "assistance",
            number: "08",
            title: "Need Assistance?",
            text: [
                "For an Amazon order's shipping, tracking, delivery, cancellation, return, replacement, or refund, please coordinate directly with Amazon through your order or Amazon's customer support options.",
            ],
        },
    ];

    return (
        <>
            <Helmet>
                <title>
                    Shipping Policy | Mohit's Shopping App
                </title>

                <meta
                    name="description"
                    content="Learn how shipping and delivery work for products purchased through Amazon affiliate links on Mohit's Shopping App."
                />

                <meta
                    name="robots"
                    content="index, follow, max-image-preview:large"
                />

                <meta name="author" content="Mohit Singh" />

                <link
                    rel="canonical"
                    href={canonicalUrl}
                />

                <meta
                    property="og:type"
                    content="website"
                />

                <meta
                    property="og:title"
                    content="Shipping Policy | Mohit's Shopping App"
                />

                <meta
                    property="og:description"
                    content="Understand shipping and delivery for products purchased through Amazon affiliate links on Mohit's Shopping App."
                />

                <meta
                    property="og:url"
                    content={canonicalUrl}
                />

                <meta
                    property="og:image"
                    content="https://myonlineshopping.co.in/favicon.png"
                />

                <meta
                    name="twitter:card"
                    content="summary_large_image"
                />

                <meta
                    name="twitter:title"
                    content="Shipping Policy | Mohit's Shopping App"
                />

                <meta
                    name="twitter:description"
                    content="Shipping information for Amazon purchases."
                />

                <meta
                    name="twitter:image"
                    content="https://myonlineshopping.co.in/favicon.png"
                />

                <script type="application/ld+json">
                    {JSON.stringify(structuredData)}
                </script>
            </Helmet>

            <main className={styles.shippingPage}>
                {/* HERO */}
                <section className={styles.hero}>
                    <div className={styles.heroGlowOne}></div>
                    <div className={styles.heroGlowTwo}></div>

                    <Container>
                        <div className={styles.breadcrumb}>
                            <span>Home</span>
                            <i>/</i>
                            <span>Legal</span>
                            <i>/</i>
                            <strong>Shipping Policy</strong>
                        </div>

                        <div className={styles.heroContent}>
                            <div className={styles.heroText}>
                                <div className={styles.eyebrow}>
                                    <span className={styles.eyebrowIcon}>
                                        🚚
                                    </span>
                                    Shipping Information
                                </div>

                                <h1>
                                    Shipping
                                    <span> Policy</span>
                                </h1>

                                <p>
                                    Important information about shipping and
                                    delivery for purchases completed through
                                    Amazon.
                                </p>

                                <div className={styles.heroMeta}>
                                    <div className={styles.metaItem}>
                                        <span className={styles.metaIcon}>
                                            ↻
                                        </span>

                                        <div>
                                            <small>Last updated</small>
                                            <strong>
                                                August 26, 2026
                                            </strong>
                                        </div>
                                    </div>

                                    <div className={styles.metaDivider}></div>

                                    <div className={styles.metaItem}>
                                        <span className={styles.metaIcon}>
                                            ✓
                                        </span>

                                        <div>
                                            <small>Shipping handled by</small>
                                            <strong>
                                                Amazon & Sellers
                                            </strong>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.heroVisual}>
                                <div className={styles.packageCard}>
                                    <div className={styles.packageTop}>
                                        <div className={styles.packageLogo}>
                                            MS
                                        </div>

                                        <div className={styles.packageLines}>
                                            <span></span>
                                            <span></span>
                                        </div>

                                        <div className={styles.deliveryCheck}>
                                            ✓
                                        </div>
                                    </div>

                                    <div className={styles.packageBox}>
                                        <div className={styles.boxTape}></div>
                                        <div className={styles.boxMark}>
                                            <span></span>
                                            <span></span>
                                        </div>
                                    </div>

                                    <div className={styles.deliveryLines}>
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </div>

                                    <div className={styles.packageFooter}>
                                        <span>SHIPPING</span>
                                        <span>2026</span>
                                    </div>
                                </div>

                                <div className={styles.floatingBadge}>
                                    <span>✓</span>
                                    Amazon Fulfillment
                                </div>
                            </div>
                        </div>
                    </Container>
                </section>

                {/* CONTENT */}
                <Container className={styles.contentContainer}>
                    <div className={styles.layout}>
                        {/* SIDEBAR */}
                        <aside className={styles.sidebar}>
                            <div className={styles.sidebarInner}>
                                <div className={styles.sidebarTitle}>
                                    <span>Contents</span>
                                    <small>8 sections</small>
                                </div>

                                <nav className={styles.toc}>
                                    {sections.map((section) => (
                                        <a
                                            key={section.id}
                                            href={`#${section.id}`}
                                        >
                                            <span>{section.number}</span>
                                            <strong>
                                                {section.title}
                                            </strong>
                                        </a>
                                    ))}
                                </nav>

                                <div className={styles.helpCard}>
                                    <div className={styles.helpIcon}>
                                        ?
                                    </div>

                                    <strong>Need help?</strong>

                                    <p>
                                        Have a question about shipping or
                                        delivery? We're happy to help.
                                    </p>

                                    <a href="https://myonlineshopping.co.in/contact-us">
                                        Contact us
                                        <span>→</span>
                                    </a>
                                </div>
                            </div>
                        </aside>

                        {/* ARTICLE */}
                        <article className={styles.article}>
                            {/* NOTICE */}
                            <div className={styles.notice}>
                                <div className={styles.noticeIcon}>
                                    🚚
                                </div>

                                <div className={styles.noticeContent}>
                                    <span className={styles.noticeLabel}>
                                        Important information
                                    </span>

                                    <h2>
                                        We do not directly ship products
                                    </h2>

                                    <p>
                                        Mohit's Shopping App does not directly
                                        ship products. Orders are completed and
                                        fulfilled through Amazon.
                                    </p>
                                </div>

                                <div className={styles.noticeArrow}>
                                    ↗
                                </div>
                            </div>

                            {/* SECTIONS */}
                            {sections.map((section) => (
                                <section
                                    key={section.id}
                                    id={section.id}
                                    className={styles.policySection}
                                >
                                    <div className={styles.sectionNumber}>
                                        {section.number}
                                    </div>

                                    <div className={styles.sectionBody}>
                                        <h2>{section.title}</h2>

                                        {section.text.map(
                                            (paragraph, index) => (
                                                <p key={index}>
                                                    {paragraph}
                                                </p>
                                            )
                                        )}

                                        {section.id === "assistance" && (
                                            <div
                                                className={
                                                    styles.contactActions
                                                }
                                            >
                                                <a
                                                    href="mailto:mahi8191singh@gmail.com"
                                                    className={
                                                        styles.contactButton
                                                    }
                                                >
                                                    <span
                                                        className={
                                                            styles.buttonIcon
                                                        }
                                                    >
                                                        ✉
                                                    </span>

                                                    <span>
                                                        <small>
                                                            Website support
                                                        </small>

                                                        <strong>
                                                            mahi8191singh@gmail.com
                                                        </strong>
                                                    </span>

                                                    <b>→</b>
                                                </a>

                                                <a
                                                    href="tel:+918191042065"
                                                    className={
                                                        styles.contactButton
                                                    }
                                                >
                                                    <span
                                                        className={
                                                            styles.buttonIcon
                                                        }
                                                    >
                                                        ☎
                                                    </span>

                                                    <span>
                                                        <small>
                                                            Call us
                                                        </small>

                                                        <strong>
                                                            +91 8191042065
                                                        </strong>
                                                    </span>

                                                    <b>→</b>
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                </section>
                            ))}

                            <div className={styles.bottomNote}>
                                <span>✓</span>
                                Shipping, delivery, tracking, and order
                                related matters are handled according to the
                                applicable Amazon policies.
                            </div>
                        </article>
                    </div>
                </Container>
            </main>
        </>
    );
};

export default ShippingPolicy;
